import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { clearSavedLocation, fetchApproxLocation, readSavedLocation, writeSavedLocation, type SavedLocation } from '@/utils/savedLocation';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, LocateFixed, X, ChevronLeft, Check, BadgeCheck, Sparkles, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { centresApi } from '@/api/centresApi';
import type { Centre } from '@/api/centresApi';
import { configApi, type ServiceDescription } from '@/api/configApi';
import { useCatalog } from '@/context/CatalogContext';
import { getDistanceKm } from '@/utils/centreStats';
import { CentreCard, CentreGrid } from '@/components/centres/CentreCard';
import { Seo } from '@/seo/Seo';
import { breadcrumbSchema, collectionPageSchema } from '@/seo/schemas';
import { resolvePathId } from '@/utils/catalogPaths';

export type { Centre } from '@/api/centresApi';

// 3-column card grid, so keep the page size a multiple of 3 for a balanced layout.
const PAGE_SIZE = 12;

export default function Centres() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [centres, setCentres] = useState<Centre[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Seed the search box from a `?q=` param so the homepage hero search lands here pre-filled.
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  // Debounced copy of `search` - drives the server-side name/address query so
  // we hit the API once the user pauses, not on every keystroke. Seed it from
  // the `?q=` param too, so arriving from the homepage hero search fires a
  // single request (with the query) instead of one empty + one with the query.
  const [debouncedSearch, setDebouncedSearch] = useState(() => (searchParams.get('q') ?? '').trim());
  const [locating, setLocating] = useState(false);

  const categories = useMemo(() => {
    const raw = searchParams.get('categories') || searchParams.get('category') || '';
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }, [searchParams]);

  const serviceFilter = useMemo(
    () => (searchParams.get('service') || '').trim(),
    [searchParams],
  );

  // When arriving from a service drill-down, `service` is a catalog slug.
  // Resolve its display name for the filter chip, and link each centre card
  // straight to that service so the user doesn't search again inside the centre.
  const { serviceBySlug } = useCatalog();
  const serviceName = serviceFilter
    ? serviceBySlug(serviceFilter)?.name ?? serviceFilter
    : '';

  // Long description for the filtered service (from mongo, path-aware). Path comes
  // from the `path` param set on the category page's service links.
  // Accepts the slug (improve-health) or the legacy id (B).
  const pathKey: 'a' | 'b' = resolvePathId(searchParams.get('path')) === 'B' ? 'b' : 'a';
  // Same filter the app has. In the URL so a filtered list can be shared.
  const homeVisitOnly = searchParams.get('home_visit') === '1';
  const setHomeVisitOnly = (on: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (on) next.set('home_visit', '1');
    else next.delete('home_visit');
    setSearchParams(next, { replace: true });
  };
  const [serviceDesc, setServiceDesc] = useState<ServiceDescription | null>(null);
  useEffect(() => {
    if (!serviceFilter) {
      setServiceDesc(null);
      return;
    }
    let active = true;
    configApi
      .getServiceDescriptions()
      .then((res) => {
        if (!active) return;
        const d = res.data?.descriptions?.[serviceFilter];
        setServiceDesc(d ? (d[pathKey] ?? d.a ?? d.b ?? null) : null);
      })
      .catch(() => active && setServiceDesc(null));
    return () => {
      active = false;
    };
  }, [serviceFilter, pathKey]);

  const [userLocation, setUserLocation] = useState<SavedLocation | null>(readSavedLocation);
  const isApproxLocation = userLocation?.source === 'ip';

  // First visit: sort around the visitor's city from their IP. No prompt.
  useEffect(() => {
    if (userLocation) return;
    let cancelled = false;
    fetchApproxLocation().then((loc) => {
      if (cancelled || !loc) return;
      setUserLocation(loc);
      writeSavedLocation(loc);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inFlight = useRef<AbortController | null>(null);

  // Debounce keystrokes into `debouncedSearch` (300ms).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async (targetPage: number) => {
    // Cancel any previous still-in-flight request (covers React StrictMode
    // double-invoke in dev and stale requests when deps change rapidly).
    inFlight.current?.abort();
    const ctrl = new AbortController();
    inFlight.current = ctrl;

    setLoading(true);
    setError(false);
    try {
      const { data } = await centresApi.list({
        categories,
        service: serviceFilter || undefined,
        q: debouncedSearch || undefined,
        home_visit: homeVisitOnly || undefined,
        page: targetPage,
        limit: PAGE_SIZE,
        lat: userLocation?.lat,
        lng: userLocation?.lng,
      }, { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      setCentres(data.centres);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
      setPage(data.page || targetPage);
    } catch (err) {
      if ((err as { name?: string })?.name === 'CanceledError' || ctrl.signal.aborted) return;
      setError(true);
    } finally {
      if (inFlight.current === ctrl) inFlight.current = null;
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [categories, serviceFilter, debouncedSearch, userLocation, homeVisitOnly]);

  useEffect(() => {
    load(1);
    // Stash the current filtered URL so any drilled-in detail page can
    // navigate forward back to /centres + this query string later.
    try {
      window.sessionStorage.setItem('centresLastSearch', window.location.search);
    } catch { /* ignore */ }
    return () => inFlight.current?.abort();
  }, [load]);

  const handleUseLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc: SavedLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude, source: 'gps' };
        setUserLocation(loc);
        writeSavedLocation(loc);
        setSearch('');
        setLocating(false);
      },
      () => setLocating(false),
    );
  };

  const clearCategoryFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('categories');
    next.delete('category');
    next.delete('service');
    setSearchParams(next, { replace: true });
  };

  const displayedCentres = useMemo(() => {
    const list = centres;

    // When user location is known, the backend has already sorted + paginated
    // nearest-first and attached distance_km. Fall back to client Haversine
    // if the server didn't attach distance (older responses / degraded).
    // Distances only for an exact fix; a city-level guess would show wrong km.
    if (userLocation && !isApproxLocation) {
      return list.map((c) => {
        if (c.distance_km != null) {
          return { ...c, distance: c.distance_km } as Centre & { distance?: number };
        }
        if (c.location?.lat != null && c.location?.lng != null) {
          return {
            ...c,
            distance: getDistanceKm(
              userLocation.lat, userLocation.lng,
              c.location.lat, c.location.lng,
            ),
          };
        }
        return { ...c };
      });
    }

    return list;
  }, [centres, userLocation, isApproxLocation]);

  return (
    <div className="mx-auto max-w-[1440px] px-5 py-10 sm:px-8 lg:px-12">
      <Seo
        title="Find Verified Physiotherapy Centres Near You | Recovry"
        description="Browse Recovry-verified physiotherapy and rehab centres across India - Delhi NCR, Mumbai, Ahmedabad, Lucknow, Bengaluru and more. Filter by speciality, sort by distance, and book a session in minutes."
        canonical="/centres"
        keywords="physiotherapy near me, physiotherapy centre, physio clinic, rehab centre, sports injury rehab, neuro rehab, back pain clinic, knee pain clinic, find physiotherapist near me"
        schema={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Find a Centre', path: '/centres' },
          ]),
          collectionPageSchema({
            name: 'Find Verified Physiotherapy Centres',
            description:
              'Directory of Recovry-verified physiotherapy and rehab centres across India.',
            url: 'https://www.recovry.in/centres',
          }),
        ]}
      />
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          Find a physiotherapy clinic near you
        </h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Browse Recovry-verified physiotherapy &amp; rehab clinics across India, compare services
          and pricing, read real patient reviews, and book a session in minutes.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <BadgeCheck className="h-3.5 w-3.5" /> Verified clinics
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Sparkles className="h-3.5 w-3.5" /> Personalized therapy
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Clock className="h-3.5 w-3.5" /> Book in minutes
          </span>
        </div>

        {(categories.length > 0 || serviceFilter) && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Showing centres for:</span>
            {serviceFilter && (
              <Badge variant="default" className="gap-1.5 text-xs">
                Service: {serviceName}
              </Badge>
            )}
            {categories.map((c) => (
              <Badge key={c} variant="secondary" className="gap-1.5 text-xs">
                {c}
              </Badge>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={clearCategoryFilter}
              className="h-7 gap-1 px-2 text-xs"
            >
              <X className="h-3 w-3" /> Clear
            </Button>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <Input
            placeholder="Search by service, centre name, or area"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setUserLocation(null); }}
            className="w-full sm:max-w-sm sm:flex-1"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleUseLocation}
            disabled={locating}
            className="shrink-0 gap-1.5"
          >
            <LocateFixed className="h-4 w-4" />
            {locating
              ? 'Locating…'
              : userLocation && !isApproxLocation
                ? 'Refresh location'
                : isApproxLocation
                  ? 'Use my exact location'
                  : 'Use my location'}
          </Button>
          <label className="flex h-9 shrink-0 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm">
            <Switch checked={homeVisitOnly} onCheckedChange={setHomeVisitOnly} aria-label="Home visits only" />
            Home visits only
          </label>
          {(search || userLocation) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSearch(''); setUserLocation(null); clearSavedLocation(); }}
              className="shrink-0"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Service description (when arriving from a service drill-down) */}
      {serviceFilter && serviceDesc && (serviceDesc.detail || (serviceDesc.bullets?.length ?? 0) > 0) && (
        <div className="mb-6 rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
          <h2 className="text-lg font-bold tracking-tight text-foreground">{serviceName}</h2>
          {serviceDesc.detail && (
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
              {serviceDesc.detail}
            </p>
          )}
          {serviceDesc.bullets && serviceDesc.bullets.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-primary">
                Common signs this could help with
              </p>
              <ul className="mt-2.5 grid gap-2 sm:grid-cols-2">
                {serviceDesc.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] leading-relaxed text-muted-foreground">
                    <Check className="mt-0.5 size-3.5 shrink-0 text-[var(--sc-teal-mid)]" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[20vh] items-center justify-center text-muted-foreground">
          Loading centres…
        </div>
      ) : error ? (
        <div className="flex min-h-[20vh] flex-col items-center justify-center gap-3 text-center">
          <p className="text-muted-foreground">
            We couldn’t load clinics right now. Please check your connection and try again.
          </p>
          <Button variant="outline" size="sm" onClick={() => load(1)}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {userLocation && (
            <p className="text-sm text-muted-foreground">
              {isApproxLocation
                ? `📍 Showing centres near ${userLocation.label ?? 'your area'} (approximate, from your network).`
                : '📍 Using your current location - nearest centres shown first.'}
            </p>
          )}
          {total > 0 && (
            <p className="text-xs text-muted-foreground">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} {total === 1 ? 'centre' : 'centres'}
            </p>
          )}
          {displayedCentres.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {debouncedSearch
                ? `No centres match “${debouncedSearch}”. Try a different name or area.`
                : categories.length > 0
                  ? 'No centres available for this category right now.'
                  : 'No centres found.'}
            </p>
          ) : (
            <CentreGrid>
            {displayedCentres.map((centre) => {
              const distance = (centre as Centre & { distance?: number }).distance;
              // Came from a service? Jump straight to that service at this centre.
              const svcIdx = serviceFilter
                ? centre.services.findIndex(
                    (s) => s.slug === serviceFilter || s.name === serviceFilter,
                  )
                : -1;
              return (
                <CentreCard
                  key={centre.id}
                  centre={centre}
                  distance={distance}
                  to={svcIdx >= 0 ? `/centres/${centre.id}/service/${svcIdx}` : undefined}
                  state={{ distance, fromSearch: window.location.search }}
                />
              );
            })}
            </CentreGrid>
          )}

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => load(page - 1)}
                disabled={page <= 1 || loading}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => load(page + 1)}
                disabled={page >= totalPages || loading}
                className="gap-1"
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
